import type { ThemeId } from './index';

/**
 * Maps puzzle product slugs to their corresponding map quality settings.
 * Preview quality is for the in-app preview, print quality is for final production.
 */
export const SIZE_TO_QUALITY: Record<string, { preview: string; print: string; aspect: string }> = {
  // A4 puzzles (21x29.7cm ratio)
  '35-piece-a4-puzzle': { preview: 'a4_preview', print: 'a4_print', aspect: '21/29.7' },
  '150-piece-a4-puzzle': { preview: 'a4_preview', print: 'a4_print', aspect: '21/29.7' },

  // A3 puzzles (29.7x42cm ratio)
  '280-piece-puzzle': { preview: 'a3_preview', print: 'a3_print', aspect: '29.7/42' },
  '500-piece-puzzle': { preview: 'a3_preview', print: 'a3_print', aspect: '29.7/42' },
  '1000-piece-custom-puzzle': { preview: 'a3_preview', print: 'a3_print', aspect: '29.7/42' },
  '54-piece-custom-puzzle-35-x-50-cm-2': { preview: 'a3_preview', print: 'a3_print', aspect: '35/50' },

  // Square puzzles (1:1 ratio)
  '50-piece-heart-puzzle': { preview: 'square_preview', print: 'square_print', aspect: '1/1' },
  '100-piece-circular-custom-puzzle': { preview: 'square_preview', print: 'square_print', aspect: '1/1' },
  '100-piece-square-custom-puzzle': { preview: 'square_preview', print: 'square_print', aspect: '1/1' },
};

/**
 * Get quality settings for a puzzle slug. Falls back to A4 if not found.
 */
export function getQualityForSlug(slug: string): { preview: string; print: string; aspect: string } {
  return SIZE_TO_QUALITY[slug] || { preview: 'a4_preview', print: 'a4_print', aspect: '21/29.7' };
}

/**
 * Map configuration stored in the preview state
 */
export interface MapConfig {
  city: string;
  country: string;
  lat?: number;
  lon?: number;
  displayCity?: string;
  displayCountry?: string;
  theme: ThemeId;
  distance: number;
  font?: string;
  /** A whole country or state (map-poster area id, e.g. IN or IN-KL, or WORLD) and its options, instead of `distance` */
  areaId?: string;
  roadDetail?: string;
  showBoundaries?: boolean;
  colourStates?: boolean;
  grid?: boolean;
  /** The customer's own places on an area map, each marked with a heart (at most 5) */
  labels?: { text: string; lat: number; lon: number }[];
  /** False leaves out the map's automatic city names */
  showCities?: boolean;
}

/**
 * Metadata stored with the cart line item for map puzzles.
 * Print-quality image is generated during order fulfillment using the config.
 */
export interface MapPuzzleMetadata {
  type: 'map-puzzle';
  previewUrl: string;
  previewCacheKey: string;
  config: MapConfig & {
    quality: string;
  };
}

/**
 * Response from the map-poster API
 */
export interface MapPosterResponse {
  success: boolean;
  presignedUrl?: string;
  cacheKey?: string;
  cacheHit?: boolean;
  /** The point the poster is centred on: a city pick's snapped centre. Saved in the order so the print matches */
  lat?: number;
  lon?: number;
  /** The country or state mapped, for an area map */
  areaId?: string;
  error?: string;
}

/**
 * Parse map puzzle metadata from cart line item
 */
export function parseMapPuzzleMetadata(metadataValue: string | undefined): MapPuzzleMetadata | null {
  if (!metadataValue) return null;

  try {
    const parsed = JSON.parse(metadataValue);

    if (parsed.type === 'map-puzzle' && parsed.config) {
      return parsed as MapPuzzleMetadata;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Stringify map puzzle metadata for storage in cart
 */
export function stringifyMapPuzzleMetadata(metadata: MapPuzzleMetadata): string {
  return JSON.stringify(metadata);
}
